import { createContext, useContext } from "react";

/**
 * The app's image sizer: the URL of `src` at most `width` pixels wide, from a host that serves
 * sizes; `src` itself from one that doesn't. The app decides which hosts those are.
 */
export type ImageSizer = (src: string, width: number) => string;

export const ImageSizerContext = createContext<ImageSizer | null>(null);

/**
 * An image's `src` and `srcSet` for drawing it `width` CSS pixels wide: a 1x and a 2x size from
 * the ImageProvider's sizer, so a screen downloads no more than it can show. Without a provider,
 * or without a width, the original alone.
 */
export function useSizedImage(src: string, width?: number): { src: string; srcSet?: string } {
  const size = useContext(ImageSizerContext);
  if (!size || width === undefined) return { src };
  const one = size(src, width);
  const two = size(src, width * 2);
  return one === src && two === src ? { src } : { src: one, srcSet: `${one} 1x, ${two} 2x` };
}
